import { useEffect, useState } from "react";
import { Volume2, RefreshCcw } from "lucide-react";
import { speak } from "@/lib/tts";
import { WORD_FAMILIES } from "@/lib/wordFamilyData";
import { randomFrom } from "@/lib/gameData";

function nextFamily(current) {
  let pick = randomFrom(WORD_FAMILIES);
  let guard = 5;
  while (current && pick.rime === current.rime && guard-- > 0) pick = randomFrom(WORD_FAMILIES);
  return pick;
}

export default function WordFamily() {
  const [family, setFamily] = useState(() => nextFamily(null));
  const [activeOnset, setActiveOnset] = useState(family.onsets[0]);

  useEffect(() => {
    setActiveOnset(family.onsets[0]);
    const t = setTimeout(() => {
      // Introduce the family: "The -at family. cat."
      speak(`The ${family.rime.toLowerCase()} family. ${family.onsets[0].toLowerCase()}${family.rime.toLowerCase()}.`, { rate: 0.85 });
    }, 250);
    return () => clearTimeout(t);
  }, [family]);

  const readWord = (onset) => {
    setActiveOnset(onset);
    const word = `${onset}${family.rime}`.toLowerCase();
    speak(word, { rate: 0.9 });
  };

  const readAll = () => {
    family.onsets.forEach((o, i) => {
      setTimeout(() => {
        setActiveOnset(o);
        speak(`${o}${family.rime}`.toLowerCase(), { rate: 0.9 });
      }, i * 900);
    });
  };

  const combined = `${activeOnset}${family.rime}`;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="inline-flex items-center gap-2 bg-orange-50 text-orange-700 px-4 py-2 rounded-full font-bold text-sm">
          {family.theme}
        </div>
        <button
          data-testid="family-next"
          onClick={() => setFamily((cur) => nextFamily(cur))}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 bg-slate-100 hover:bg-slate-200 font-bold text-sm text-slate-700 transition-colors"
        >
          <RefreshCcw className="w-4 h-4" /> New family
        </button>
      </div>

      <div className="text-center bg-slate-50 rounded-3xl p-6 sm:p-10 mb-6">
        <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-2">
          Change the first sound &mdash; make a new word.
        </p>
        <div
          data-testid="family-word"
          className="font-display text-7xl sm:text-8xl font-semibold text-slate-800 leading-none"
        >
          <span key={activeOnset} className="text-orange-500 phonx-letter-pop inline-block">
            {activeOnset}
          </span>
          <span className="text-slate-800">{family.rime}</span>
        </div>
        <button
          data-testid="family-play-word"
          onClick={() => readWord(activeOnset)}
          className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-bold shadow-md hover:shadow-lg transition-all"
        >
          <Volume2 className="w-4 h-4" /> Say &ldquo;{combined.toLowerCase()}&rdquo;
        </button>
      </div>

      <p className="text-center text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">
        Tap an onset to swap the beginning sound
      </p>
      <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-6">
        {family.onsets.map((o, i) => (
          <button
            key={`onset-${o}`}
            data-testid={`family-onset-${i}`}
            onClick={() => readWord(o)}
            className={`min-w-[54px] h-14 px-3 rounded-2xl border-2 font-display text-2xl font-semibold transition-all
              ${activeOnset === o
                ? "bg-orange-500 border-orange-600 text-white shadow-md scale-105"
                : "bg-white border-slate-200 text-slate-800 hover:border-orange-400 hover:text-orange-600 hover:-translate-y-0.5 shadow-sm hover:shadow-md"
              }
            `}
          >
            {o}
          </button>
        ))}
      </div>

      <div className="flex justify-center">
        <button
          data-testid="family-read-all"
          onClick={readAll}
          className="rounded-full px-6 py-3 bg-white border-2 border-slate-200 hover:border-orange-400 text-slate-800 font-bold transition-colors inline-flex items-center gap-2"
        >
          <Volume2 className="w-5 h-5" /> Read the whole family
        </button>
      </div>
    </div>
  );
}
